const KEY = 'et.hadSession';

/**
 * A per-browser marker recording that an authenticated session was once established here.
 *
 * It exists to tell an *involuntary expiry* apart from a *first visit* on the one path a user can
 * actually observe today: a page reload (BUG-007). A reload empties the in-memory session store, so
 * an in-memory `reason` cannot survive it — the store re-bootstraps from `loading`, and a bare
 * `GET /api/v1/me` 401 on that cold boot looks identical whether the user never signed in or was
 * signed in a moment ago. This marker is the one bit that persists across the reload: set on a
 * successful `/me`, cleared on a voluntary sign-out, and read when a 401 settles the store.
 *
 * Every access is wrapped: `localStorage` throws in private-mode / storage-disabled browsers, and a
 * throw here must degrade to today's silent behaviour (no notice), never break the session bootstrap.
 */
export function markPriorSession(): void {
    try {
        localStorage.setItem(KEY, '1');
    } catch {
        // Storage unavailable — expiry just stays indistinguishable from a first visit, which is
        // exactly the pre-fix behaviour. Nothing to recover.
    }
}

/** Clear the marker — called on a voluntary sign-out so the next cold-boot 401 reads as a first visit. */
export function clearPriorSession(): void {
    try {
        localStorage.removeItem(KEY);
    } catch {
        // Storage unavailable — see markPriorSession.
    }
}

/** True when this browser recorded a prior authenticated session that has not been signed out of. */
export function hadPriorSession(): boolean {
    try {
        return localStorage.getItem(KEY) === '1';
    } catch {
        return false;
    }
}
